/* eslint-disable no-undef */
import { Loader } from '@googlemaps/js-api-loader';
import SvgIcon from '@png/npm-svg-icon';
import ApiManager from '../api/crud-manager';
import utils from '../utils';

const onRenderError = (module, error) => {
  module.classList.remove('is-loading');
  module.classList.add('is-error');
  console.error(error);
};

/**
 * Course selector module
 * @class
 * @author Max Swinkels <max.swinkels@pangaea.nl>
 */
class CourseSelector {
  constructor(options = {}) {
    this.config = {
      module: document.querySelector('[data-module="courses-selector"]'),
      googleMapsApiKey: 'AIzaSyAGDGynciJ88nQ3q91qPHZtLLWdqEQ8t_s',
      microServicesEndpoint: 'https://microservice-hok.pangaeatest.nl',
      timeoutDelay: 500,
      // default geo location is set to the center of The Netherlands (Utrecht)
      geoLocation: {
        lat: '52.2129919',
        long: '5.2793703',
        range: 250,
        city: ''
      },
      ...options
    };
    this.apiManager = new ApiManager();
  }

  /**
   * Sets event listeners on all location buttons
   * Navigate to next step in course selector
   * @param module
   */
  chooseLocationListener(module) {
    const selectLocationButton = module.querySelectorAll('[data-module="select-location"]');

    selectLocationButton.forEach(button => {
      button.addEventListener('click', event => {
        event.preventDefault();

        const { locationId } = button.dataset;

        this.selectedLocationId = locationId;

        this.renderLocationDataList(module);
      });
    });
  }

  /**
   * When a radius is selected
   * Get values from select and set geo location range config and trigger a new render
   * @param module
   */
  radiusListener(module) {
    const { geoLocation } = this.config;
    const radiusField = module.querySelector('[name="courses-search-radius"]');

    if (!radiusField) {
      return;
    }

    radiusField.addEventListener('change', event => {
      event.preventDefault();
      const radius = event.target.value;

      if (radius && geoLocation.city.length) {
        this.config.geoLocation.range = radiusField.value;
        this.onRenderLocationsDebounce(module);
      }
    });
  }

  /**
   * Load google maps places API
   * Used on load and 'on change' address field
   * @param module
   */
  geoLocationListener(module) {
    const { googleMapsApiKey } = this.config;
    const locationField = module.querySelector('[name="courses-search-geo"]');
    const radiusField = module.querySelector('[name="courses-search-radius"]');

    if (!locationField) {
      return;
    }

    const googleMapsLoader = new Loader({
      apiKey: googleMapsApiKey,
      libraries: ['places']
    });

    const options = {
      componentRestrictions: { country: ['nl', 'be'] }
    };

    googleMapsLoader
      .load()
      .then(() => {
        const { google } = window;
        const autocomplete = new google.maps.places.Autocomplete(locationField, options);

        google.maps.event.addListener(autocomplete, 'place_changed', () => {
          const place = autocomplete.getPlace();

          if (!place.geometry) {
            console.error(`No details available for input: "${place.name}"`);
            return;
          }

          this.config.geoLocation.lat = place.geometry.location.lat();
          this.config.geoLocation.long = place.geometry.location.lng();
          this.config.geoLocation.city = place.vicinity;

          if (radiusField) {
            this.config.geoLocation.range = radiusField.value;
          }

          this.onRenderLocationsDebounce(module);
        });

        // select first item on enter
        google.maps.event.addDomListener(locationField, 'keydown', event => {
          if (event.keyCode === 13 && !event.triggered) {
            event.preventDefault();
            event.stopPropagation();

            const suggestionSelected = document.querySelectorAll('.pac-item-selected').length > 0;
            const results = document.querySelectorAll('.pac-item').length > 0;

            if (!suggestionSelected && results) {
              const ex1 = new Event('keydown');
              ex1.code = 'ArrowDown';
              ex1.key = 'ArrowDown';
              ex1.keyCode = 40;
              google.maps.event.trigger(event.target, 'keydown', ex1);

              const ex2 = new Event('keydown');
              ex2.code = 'Enter';
              ex2.key = 'Enter';
              ex2.keyCode = 13;
              ex2.triggered = true;
              google.maps.event.trigger(event.target, 'keydown', ex2);
            } else {
              locationField.value = '';
            }
          }
        });
      })
      .catch(error => {
        console.error(error);
      });
  }

  /**
   * Async request locations from microservices
   * Called on load and 'on change' location or radius
   */
  async getLocations(geoLocation, itemCode) {
    const { microServicesEndpoint } = this.config;

    const parameters = {};
    parameters.latitude = geoLocation.lat;
    parameters.longitude = geoLocation.long;
    parameters.range = geoLocation.range;
    parameters.$filter = `ItemCode eq '${itemCode}' and Sold lt Stock`;
    parameters.$orderby = 'DateTimeStart ASC';

    const url = `${microServicesEndpoint}/api/locationInRange${utils.buildUrlParameters(
      parameters
    )}`;

    const response = await this.apiManager.get(url).then(data => data);
    const data = await response;

    return data;
  }

  /**
   * Get locations based on address and radius
   * Make API call and update DOM with location list HTML
   * @param module
   */
  renderLocationsList(module) {
    const { geoLocation } = this.config;

    // check if location has itemcode
    const courseItemCodeField = module.querySelector('[name="courses-search-course-itemcode"]');
    if (!courseItemCodeField && courseItemCodeField.value) {
      console.error('Product id does not exists');
      module.classList.add('is-error');
      return;
    }

    module.classList.add('is-loading');
    module.classList.remove('is-error');

    const itemCode = courseItemCodeField.value;
    const hasSearchLocation = geoLocation.city.length; // check if user has entered location

    const locationListPlaceholder = module.querySelector('[data-location-list]');
    const searchResultsLabel = module.querySelector('[data-courses-search-results]');
    const searchAmountPlaceholder = module.querySelector('[data-courses-search-amount]');
    const searchCityPlaceholder = module.querySelector('[data-courses-search-city]');

    this.getLocations(geoLocation, itemCode)
      .then(locationsResponse => {
        const uniqueLocations = utils.uniqueArrayOfObjects(locationsResponse, 'name'); // remove all duplicate locations so that only the first date of a certain location is shown

        this.locationsResult = locationsResponse; // set location results in memory

        // set view data & settings
        const templateView = 'CoursesLocationList';
        const viewData = {
          locations: uniqueLocations,
          hideDistance: !hasSearchLocation // hide distance in template view if users hasn't entered a location
        };

        utils
          .jsonToView(viewData, templateView)
          .then(html => {
            // show result amount in city if users has entered a location
            if (searchResultsLabel && hasSearchLocation) {
              searchResultsLabel.style.display = 'block';
              searchAmountPlaceholder.innerHTML = uniqueLocations.length;
              searchCityPlaceholder.innerHTML = geoLocation.city;
            }

            module.classList.remove('is-loading');
            locationListPlaceholder.innerHTML = html;

            this.chooseLocationListener(module);

            new SvgIcon().init(); // trigger svgIcons after the api has added all products to the html
          })
          .catch(error => {
            onRenderError(module, error);
          });
      })
      .catch(error => {
        onRenderError(module, error);
      });
  }

  /**
   * Get dates based on course location
   * Location result from previous api, filter on selected location id
   * And update DOM with date list HTML
   * @param module
   */
  // eslint-disable-next-line class-methods-use-this
  renderLocationDataList(module) {
    const locationsResult = this.locationsResult || [];
    const locationId = this.selectedLocationId;

    const locationDataListPlaceholder = module.querySelector('[data-location-data-list]');
    const stepBackButton = module.querySelector('[data-course-step-back]');

    stepBackButton.addEventListener('click', event => {
      event.preventDefault();
      $('a[href="#tab1"]').tab('show'); // navigate back to locations tab (jQuery needed because of bootstrap)
    });

    locationDataListPlaceholder.innerHTML = ''; // clear previous data results

    module.classList.add('is-loading');
    module.classList.remove('is-error');

    $('a[href="#tab2"]').tab('show'); // navigate to select date tab (jQuery needed because of bootstrap)

    const selectedLocationData = locationsResult.filter(obj => obj.id.toString() === locationId); // Filter location results array on selected location id (product itemCode)

    // set view data & settings
    const templateViewName = 'CoursesLocationDataList';
    const viewData = {
      locations: selectedLocationData
    };

    utils
      .jsonToView(viewData, templateViewName)
      .then(html => {
        module.classList.remove('is-loading');
        locationDataListPlaceholder.innerHTML = html;

        // TODO: Build new add to cart api
        // eslint-disable-next-line no-undef
        cart.addEventListener(); // trigger ancient cart module

        new SvgIcon().init(); // trigger svgIcons after the api has added all products to the html
      })
      .catch(error => {
        onRenderError(module, error);
      });
  }

  /**
   * Triggers renderLocationsList after a configured delay
   * This to prevent overloading the API with every location or radius change
   * For better performance and UX
   * @param module
   */
  onRenderLocationsDebounce(module) {
    const { timeoutDelay } = this.config;

    if (this.renderLocationsTimeout) {
      clearTimeout(this.renderLocationsTimeout);
    }

    this.renderLocationsTimeout = setTimeout(() => {
      this.renderLocationsList(module);
    }, timeoutDelay);
  }

  init() {
    const { module } = this.config;

    if (!module) {
      return;
    }

    // set listener after modal opens (jQuery needed because of bootstrap)
    $(document).on('show.bs.modal', '#modal-sidepanel', () => {
      this.geoLocationListener(module);
      this.radiusListener(module);
    });

    // initial load locations
    this.renderLocationsList(module);
  }
}

export default CourseSelector;
